import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import { callRpc } from '@/services/juntasRpc'

const KYC_BUCKET = 'kyc-private'
const PROPIO_KYC_BUCKET = 'propio-kyc-private'

export async function checkIsModerator() {
  if (!isSupabaseConfigured || !supabase) return false

  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return false

  const { data } = await supabase
    .from('moderators')
    .select('user_id')
    .eq('user_id', user.id)
    .eq('active', true)
    .maybeSingle()

  return Boolean(data)
}

export async function fetchKycModerationQueue() {
  const data = await callRpc('get_kyc_moderation_queue')
  return Array.isArray(data) ? data : []
}

export async function markKycInReview(kycId) {
  return callRpc('mark_kyc_in_review', { p_kyc_id: kycId })
}

export async function approveKycSubmission(kycId, peerReviewerId = null) {
  return callRpc('approve_kyc_submission', {
    p_kyc_id: kycId,
    p_peer_reviewer_id: peerReviewerId,
  })
}

/**
 * Envía el email de confirmación de verificación a la usuaria.
 * Best-effort: el destinatario y el estado se derivan en el servidor (Edge
 * Function), solo se pasa el user_id. Lanza si el envío falla, para que la UI
 * pueda avisar — pero NO debe revertir la aprobación.
 */
export async function notifyKycApproved(userId, { force = false } = {}) {
  if (!supabase) throw new Error('Supabase no configurado')
  const { data, error } = await supabase.functions.invoke('kyc-approved-notification', {
    body: { user_id: userId, force },
  })
  if (error) {
    let message = error.message
    try {
      const ctx = await error.context?.json?.()
      if (ctx?.error) message = ctx.error
    } catch {
      // contexto no parseable
    }
    throw new Error(message)
  }
  return data
}

export async function fetchActiveModerators() {
  const data = await callRpc('list_active_moderators')
  return Array.isArray(data) ? data : []
}

/**
 * Envía a la usuaria un email relativo a su videollamada de verificación.
 * Best-effort (igual que notifyKycApproved). El destinatario y los detalles se
 * derivan en el servidor desde session_id.
 * @param {string} sessionId
 * @param {'invitation'|'reschedule'|'cancellation'|'not_passed'} kind
 * @param {string|null} userMessage  Mensaje opcional para la usuaria (solo 'not_passed').
 */
export async function notifyViaCScheduled(sessionId, kind = 'invitation', userMessage = null) {
  if (!supabase) throw new Error('Supabase no configurado')
  const { data, error } = await supabase.functions.invoke('via-c-invitation', {
    body: { session_id: sessionId, kind, user_message: userMessage },
  })
  if (error) {
    let message = error.message
    try {
      const ctx = await error.context?.json?.()
      if (ctx?.error) message = ctx.error
    } catch {
      // contexto no parseable
    }
    throw new Error(message)
  }
  return data
}

/** Reprograma una videollamada Vía C planificada (moderación). */
export async function rescheduleViaCSession({ sessionId, scheduledAt, meetingUrl, moderatorId, peerId = null }) {
  return callRpc('reschedule_via_c_session', {
    p_session_id: sessionId,
    p_scheduled_at: scheduledAt,
    p_meeting_url: meetingUrl,
    p_moderator_id: moderatorId,
    p_peer_id: peerId,
  })
}

/** Cancela una videollamada Vía C planificada (moderación). */
export async function cancelViaCSession({ sessionId, reason = null }) {
  return callRpc('cancel_via_c_session', {
    p_session_id: sessionId,
    p_reason: reason,
  })
}

/**
 * Marca una videollamada Vía C como NO superada: rechaza el KYC con motivo
 * interno obligatorio (visible para super admin) y deja el perfil en 'rejected'.
 */
export async function failViaCSession({ sessionId, internalReason, userMessage = null }) {
  return callRpc('fail_via_c_session', {
    p_session_id: sessionId,
    p_internal_reason: internalReason,
    p_user_message: userMessage,
  })
}

/** Sesiones Vía C de la moderadora actual (principal u observadora) para su agenda. */
export async function fetchMyViaCSessions({ from = null, to = null } = {}) {
  // No pasar null: sobreescribiría los DEFAULT de la RPC y `>= NULL` excluye todo.
  const params = {}
  if (from) params.p_from = from
  if (to) params.p_to = to
  const data = await callRpc('list_my_via_c_sessions', params)
  return Array.isArray(data) ? data : []
}

export async function scheduleViaCSession({
  userId,
  scheduledAt,
  meetingUrl,
  moderatorId,
  peerId = null,
}) {
  return callRpc('schedule_via_c_session', {
    p_user_id: userId,
    p_scheduled_at: scheduledAt,
    p_meeting_url: meetingUrl,
    p_moderator_id: moderatorId,
    p_peer_id: peerId,
  })
}

export async function completeViaCSession({
  sessionId,
  outcome,
  outcomeNotes = null,
  cartaAligned = true,
  communityPresented = true,
}) {
  return callRpc('complete_via_c_session', {
    p_session_id: sessionId,
    p_outcome: outcome,
    p_outcome_notes: outcomeNotes,
    p_carta_aligned: cartaAligned,
    p_community_presented: communityPresented,
  })
}

export async function rejectKycSubmission(kycId, rejectionReason, rejectionNotes = null) {
  return callRpc('reject_kyc_submission', {
    p_kyc_id: kycId,
    p_rejection_reason: rejectionReason,
    p_rejection_notes: rejectionNotes,
  })
}

/** Convertit `kyc-private/uuid/file.jpg` → path objet Storage `uuid/file.jpg` */
export function storagePathFromKycRef(storageRef) {
  if (!storageRef) return null
  const prefix = `${KYC_BUCKET}/`
  return storageRef.startsWith(prefix) ? storageRef.slice(prefix.length) : storageRef
}

export function isPdfStorageRef(storageRef) {
  if (!storageRef) return false
  return /\.pdf$/i.test(storageRef)
}

export async function createKycSignedUrl(storageRef, expiresIn = 900) {
  if (!supabase || !storageRef) return null

  const path = storagePathFromKycRef(storageRef)

  // 1. Tentative signed URL
  const { data, error } = await supabase.storage.from(KYC_BUCKET).createSignedUrl(path, expiresIn)
  if (!error) return data.signedUrl

  console.warn('[JUNTAS admin] createSignedUrl failed, trying download():', path, error.message)

  // 2. Fallback fetch direct avec Authorization (contourne le check signed-URL)
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (session?.access_token) {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
      const encodedPath = path.split('/').map(encodeURIComponent).join('/')
      const res = await fetch(
        `${supabaseUrl}/storage/v1/object/authenticated/${KYC_BUCKET}/${encodedPath}`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            apikey: anonKey,
          },
        },
      )
      if (res.ok) {
        const blob = await res.blob()
        return URL.createObjectURL(blob)
      }
      console.warn('[JUNTAS admin] fetch fallback status:', res.status)
    }
  } catch (fetchErr) {
    console.warn('[JUNTAS admin] fetch fallback error:', fetchErr)
  }

  // 3. Fallback supabase download()
  const { data: blob, error: dlError } = await supabase.storage.from(KYC_BUCKET).download(path)
  if (!dlError) return URL.createObjectURL(blob)

  console.error('[JUNTAS admin] all fallbacks failed:', path, dlError)
  throw error
}

export function kycStorageDashboardUrl(storageRef) {
  const projectRef = import.meta.env.VITE_SUPABASE_URL?.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1]
  if (!projectRef || !storageRef) return null
  const path = storagePathFromKycRef(storageRef)
  return `https://supabase.com/dashboard/project/${projectRef}/storage/buckets/${KYC_BUCKET}?path=${encodeURIComponent(path)}`
}

export function storagePathFromPropioKycRef(storageRef) {
  if (!storageRef) return null
  const prefix = `${PROPIO_KYC_BUCKET}/`
  return storageRef.startsWith(prefix) ? storageRef.slice(prefix.length) : storageRef
}

export async function fetchPropioKycModerationQueue() {
  const data = await callRpc('get_propio_kyc_moderation_queue')
  return Array.isArray(data) ? data : []
}

export async function markPropioKycInReview(submissionId) {
  return callRpc('mark_propio_kyc_in_review', { p_submission_id: submissionId })
}

export async function approvePropioKycSubmission(submissionId) {
  return callRpc('approve_propio_kyc', { p_submission_id: submissionId })
}

export async function rejectPropioKycSubmission(submissionId, rejectionReason, rejectionNotes = null) {
  return callRpc('reject_propio_kyc', {
    p_submission_id: submissionId,
    p_rejection_reason: rejectionReason,
    p_rejection_notes: rejectionNotes,
  })
}

export async function createPropioKycSignedUrl(storageRef, expiresIn = 900) {
  if (!supabase || !storageRef) return null
  const path = storagePathFromPropioKycRef(storageRef)
  const { data, error } = await supabase.storage
    .from(PROPIO_KYC_BUCKET)
    .createSignedUrl(path, expiresIn)
  if (error) {
    console.error('[JUNTAS admin] propio signed URL failed:', path, error)
    throw error
  }
  return data.signedUrl
}

// ── Reportes (S3) ─────────────────────────────────────────────────────

export async function fetchReportsQueue(priority = null, limit = 50) {
  const data = await callRpc('get_reports_queue', { p_priority: priority, p_limit: limit })
  return Array.isArray(data) ? data : []
}

export async function takeReport(reportId) {
  return callRpc('take_report', { p_report_id: reportId })
}

export async function resolveReport({
  reportId,
  targetUserId,
  action,
  justification,
  peerReviewerId = null,
  internalNotes = null,
}) {
  return callRpc('resolve_report', {
    p_report_id: reportId,
    p_target_user_id: targetUserId,
    p_action: action,
    p_justification: justification,
    p_peer_reviewer_id: peerReviewerId,
    p_internal_notes: internalNotes,
  })
}

export async function fetchModerationStats() {
  return callRpc('get_moderation_stats')
}

// ── Super admin (S3) ──────────────────────────────────────────────────

export async function checkIsSuperAdmin() {
  if (!isSupabaseConfigured || !supabase) return false
  try {
    const data = await callRpc('is_super_admin')
    return Boolean(data)
  } catch {
    return false
  }
}

export async function superAdminListAllModerators() {
  const data = await callRpc('list_all_moderators')
  return Array.isArray(data) ? data : []
}

/**
 * Invita una moderadora por email: crea la cuenta si no existe, envía un email
 * de invitación brandado (Resend) con enlace para definir contraseña, y activa
 * el rol. Si la cuenta ya existe, solo activa el rol y avisa por email.
 * Ejecuta la Edge Function `invite-moderator` (requiere service_role del lado servidor).
 */
export async function superAdminInviteModerator(email, role = 'moderator') {
  return invokeInviteModerator({ email, role })
}

/**
 * Reenvía el enlace de acceso a una moderadora que ya existe pero no definió
 * su contraseña (o la perdió). Envía un enlace recovery brandado vía Resend.
 */
export async function superAdminResendModeratorInvite(email, role = 'moderator') {
  return invokeInviteModerator({ email, role, resend: true })
}

async function invokeInviteModerator(body) {
  if (!supabase) throw new Error('Supabase no configurado')
  const { data, error } = await supabase.functions.invoke('invite-moderator', { body })
  if (error) {
    let message = error.message
    try {
      const ctx = await error.context?.json?.()
      if (ctx?.error) message = ctx.error
    } catch {
      // contexto no parseable: nos quedamos con error.message
    }
    throw new Error(message)
  }
  return data
}

export async function superAdminRevokeModerator(userId, reason = null) {
  return callRpc('revoke_moderator', { p_user_id: userId, p_reason: reason })
}

export async function superAdminListAllUsers(identityStatus = null, limit = 100, offset = 0) {
  const data = await callRpc('sa_list_all_users', {
    p_identity_status: identityStatus,
    p_limit: limit,
    p_offset: offset,
  })
  return Array.isArray(data) ? data : []
}

// ── Gestión de usuarias por el equipo (moderación + super admin, S065) ──

export async function adminListUsers(identityStatus = null, limit = 100, offset = 0) {
  const data = await callRpc('admin_list_users', {
    p_identity_status: identityStatus,
    p_limit: limit,
    p_offset: offset,
  })
  return Array.isArray(data) ? data : []
}

export async function adminGetUserProfile(userId) {
  return callRpc('admin_get_user_profile', { p_user_id: userId })
}

export async function adminUpdateUserProfile(userId, patch) {
  return callRpc('admin_update_user_profile', { p_user_id: userId, p_patch: patch })
}

export async function superAdminListAllPropios(status = null, limit = 100, offset = 0) {
  const data = await callRpc('sa_list_all_propios', {
    p_status: status,
    p_limit: limit,
    p_offset: offset,
  })
  return Array.isArray(data) ? data : []
}

export async function superAdminRevokeKyc(userId, reason, notes = null) {
  return callRpc('sa_revoke_kyc', { p_user_id: userId, p_reason: reason, p_notes: notes })
}

/** Reactiva (verifica) a una usuaria rechazada — override super admin. */
export async function superAdminReactivateUser(userId) {
  return callRpc('sa_reactivate_user', { p_user_id: userId })
}

/**
 * Avisa a la usuaria de la suspensión de su cuenta (revocación). Best-effort.
 * Solo se llama cuando la super admin lo decide explícitamente (caso benigno).
 * El destinatario se deriva en el servidor; el mensaje es opcional.
 */
export async function notifyKycRevoked(userId, message = null) {
  if (!supabase) throw new Error('Supabase no configurado')
  const { data, error } = await supabase.functions.invoke('kyc-revoked-notification', {
    body: { user_id: userId, message },
  })
  if (error) {
    let m = error.message
    try {
      const ctx = await error.context?.json?.()
      if (ctx?.error) m = ctx.error
    } catch {
      // contexto no parseable
    }
    throw new Error(m)
  }
  return data
}

// ── Admin asociaciones (S4) ────────────────────────────────────────────

export async function adminListOrganizations(type = null, active = null, limit = 50, offset = 0) {
  const data = await callRpc('admin_list_organizations', {
    p_type: type,
    p_active: active,
    p_limit: limit,
    p_offset: offset,
  })
  return Array.isArray(data) ? data : []
}

export async function adminActivateOrganization(orgId, antiTerfVerified, notes = null) {
  return callRpc('admin_activate_organization', {
    p_org_id: orgId,
    p_anti_terf_verified: antiTerfVerified,
    p_notes: notes,
  })
}

export async function adminDeactivateOrganization(orgId, reason) {
  return callRpc('admin_deactivate_organization', { p_org_id: orgId, p_reason: reason })
}

export async function adminAddOrgAdminByEmail(orgId, email, role = 'admin', displayName = null) {
  return callRpc('admin_add_org_admin_by_email', {
    p_org_id: orgId,
    p_email: email,
    p_role: role,
    p_display_name: displayName,
  })
}

export async function adminAddOrgAdmin(orgId, userId, role = 'admin', displayName = null) {
  return callRpc('admin_add_org_admin', {
    p_org_id: orgId,
    p_user_id: userId,
    p_role: role,
    p_display_name: displayName,
  })
}

export async function adminRemoveOrgAdmin(orgId, userId) {
  return callRpc('admin_remove_org_admin', { p_org_id: orgId, p_user_id: userId })
}

// ── Admin mediación (S6) ───────────────────────────────────────────────

export async function adminGetMediacionQueue(status = null, limit = 50, offset = 0) {
  const data = await callRpc('admin_get_mediation_queue', {
    p_status: status,
    p_limit: limit,
    p_offset: offset,
  })
  return Array.isArray(data) ? data : []
}

export async function adminTakeMediacionTicket(ticketId) {
  return callRpc('admin_take_mediation_ticket', { p_ticket_id: ticketId })
}

export async function adminResolveMediacionTicket(ticketId, resolutionNotes = null) {
  return callRpc('admin_resolve_mediation_ticket', {
    p_ticket_id: ticketId,
    p_resolution_notes: resolutionNotes,
  })
}

// ── Admin dossiers validations (S6) ───────────────────────────────────

export async function adminGetDossierValidationsQueue(limit = 30) {
  const data = await callRpc('admin_get_dossier_validations_queue', { p_limit: limit })
  return Array.isArray(data) ? data : []
}

export async function adminApproveDossierValidation(validationId, notes = null) {
  return callRpc('admin_approve_dossier_validation', {
    p_validation_id: validationId,
    p_notes: notes,
  })
}

export async function adminBlockDossierValidation(validationId, reason = null) {
  return callRpc('admin_block_dossier_validation', {
    p_validation_id: validationId,
    p_reason: reason,
  })
}

// ── Admin sello (S6) ──────────────────────────────────────────────────

export async function adminGetSelloAuditLog(ofertaId = null, limit = 50) {
  const data = await callRpc('admin_get_sello_audit_log', {
    p_oferta_id: ofertaId,
    p_limit: limit,
  })
  return Array.isArray(data) ? data : []
}

export async function adminWithdrawSello(ofertaId, reason) {
  return callRpc('admin_withdraw_sello', { p_oferta_id: ofertaId, p_reason: reason })
}

export async function adminRestoreSello(ofertaId, reason = null) {
  return callRpc('admin_restore_sello', { p_oferta_id: ofertaId, p_reason: reason })
}
